import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SocioResponse } from '../../../models/socioResponse.model';
import { AtualizarProLaboreSocioRequest, ProLaboreSocioEdicaoResponse } from '../../../models/proLaboreSocio.model';
import { SociosService } from '../../../services/socios.service';
import { SpinnerService } from '../../../services/spinner.service';
import { ToastService } from '../../../services/toast.service';
import { SeveridadeToast } from '../../../models/enums/severidade-toast.enum';
import { JwtHelper } from '../../../helpers/jwtDecode.helper';

@Component({
  selector: 'app-socios-editar-prolabore',
  templateUrl: './socios-editar-prolabore.component.html',
  styleUrls: ['./socios-editar-prolabore.component.scss']
})
export class SociosEditarProlaboreComponent implements OnChanges {
  @Input() socio: SocioResponse | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<ProLaboreSocioEdicaoResponse>();

  readonly simNaoOptions = [
    { label: 'Sim', value: true },
    { label: 'Não', value: false }
  ];

  readonly diasPagamentoOptions = Array.from({ length: 31 }, (_, index) => ({
    label: `${index + 1}`,
    value: index + 1
  }));

  readonly hoje = new Date();
  readonly minCompetencia = new Date(this.hoje.getFullYear(), this.hoje.getMonth(), 1);

  isLoading = false;
  isSaving = false;
  usuarioId: string;

  form = new FormGroup({
    competencia: new FormControl<Date | null>(null, Validators.required),
    vaiRetirarProLabore: new FormControl(false, Validators.required),
    proLaboreMensal: new FormControl<number | null>(null),
    inssSobreProLabore: new FormControl<number | null>({ value: null, disabled: true }),
    irrfSobreProLabore: new FormControl<number | null>({ value: null, disabled: true }),
    valorLiquidoProLabore: new FormControl<number | null>({ value: null, disabled: true }),
    participacaoSocietaria: new FormControl<number | null>(null),
    duploVinculo: new FormControl<boolean | null>(null),
    diaPagamentoProLabore: new FormControl<number | null>(null),
    dataInicioValidade: new FormControl<Date | null>(null),
  });

  constructor(
    private readonly sociosService: SociosService,
    private readonly spinnerService: SpinnerService,
    private readonly toastService: ToastService,
    private readonly jwtHelper: JwtHelper
  ) {
    this.usuarioId = this.jwtHelper.getUserIdFromToken();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['socio']?.currentValue) {
      this.resetForm();
      this.carregarCompetenciaAtual();
    }
  }

  onCompetenciaChange(date: Date | null): void {
    if (!this.socio || !date) {
      return;
    }

    const competenciaMes = date.getMonth() + 1;
    const competenciaAno = date.getFullYear();

    this.isLoading = true;
    this.spinnerService.show('Carregando dados do pró-labore...');
    this.sociosService.obterProLaboreSocio(this.socio.id, competenciaMes, competenciaAno).subscribe({
      next: (response) => {
        this.preencherFormulario(response);
        this.sincronizarDataInicioValidadeComCompetencia();
        this.isLoading = false;
        this.spinnerService.hide();
      },
      error: (error) => {
        this.isLoading = false;
        this.spinnerService.hide();
        this.toastService.setMensagemToastErro(error, 'Não foi possível carregar os dados de pró-labore.');
      }
    });
  }

  onVaiRetirarChange(value: boolean): void {
    const campos = ['proLaboreMensal', 'participacaoSocietaria', 'duploVinculo', 'diaPagamentoProLabore'];

    campos.forEach(campo => {
      const control = this.form.get(campo);
      if (!value) {
        control?.setValue(null);
        control?.clearValidators();
      } else {
        control?.setValidators(Validators.required);
      }
      control?.updateValueAndValidity();
    });

    if (!value) {
      this.form.patchValue({
        inssSobreProLabore: null,
        irrfSobreProLabore: null,
        valorLiquidoProLabore: null
      });
      return;
    }

    const competencia = this.form.get('competencia')?.value;
    if (competencia) {
      this.form.get('dataInicioValidade')?.setValue(competencia);
    }
  }

  onProLaboreMensalBlur(): void {
    this.recalcularProLabore(true);
  }

  recalcularProLabore(silent: boolean = false): void {
    const valor = this.form.get('proLaboreMensal')?.value;
    if (!valor || valor <= 0) {
      this.form.patchValue({
        inssSobreProLabore: null,
        irrfSobreProLabore: null,
        valorLiquidoProLabore: null
      });

      if (!silent) {
        this.toastService.setMensagemToast('Informe um pró-labore mensal válido para recalcular.', SeveridadeToast.Warn);
      }

      return;
    }

    const numeroDependentes = this.socio?.numeroDependentes ?? 0;

    this.sociosService.calcularProLabore(valor, numeroDependentes).subscribe({
      next: response => {
        this.form.patchValue({
          inssSobreProLabore: response.inss,
          irrfSobreProLabore: response.irrf,
          valorLiquidoProLabore: response.valorLiquido
        });
      },
      error: error => this.toastService.setMensagemToastErro(error, 'Não foi possível recalcular o pró-labore.')
    });
  }

  salvar(): void {
    if (!this.socio) {
      return;
    }

    if (this.isSaveDisabled) {
      this.form.markAllAsTouched();
      this.toastService.setMensagemToast('Preencha os campos obrigatórios para salvar.', SeveridadeToast.Warn);
      return;
    }

    const competencia = this.form.get('competencia')?.value ?? new Date(this.hoje.getFullYear(), this.hoje.getMonth(), 1);
    const dataInicioValidade = competencia;
    const vaiRetirarProLabore = this.form.get('vaiRetirarProLabore')?.value ?? false;

    if (vaiRetirarProLabore && !this.isValidDate(competencia)) {
      this.toastService.setMensagemToast('Selecione a competência.', SeveridadeToast.Warn);
      return;
    }

    const request: AtualizarProLaboreSocioRequest = {
      socioId: this.socio.id,
      usuarioId: this.usuarioId,
      vaiRetirarProLabore,
      competenciaMes: competencia.getMonth() + 1,
      competenciaAno: competencia.getFullYear(),
      dataInicioValidadeMes: dataInicioValidade.getMonth() + 1,
      dataInicioValidadeAno: dataInicioValidade.getFullYear(),
      proLaboreMensal: this.form.get('proLaboreMensal')?.value ?? null,
      numeroDependentes: this.socio.numeroDependentes ?? 0,
      participacaoSocietaria: this.form.get('participacaoSocietaria')?.value ?? null,
      duploVinculo: this.form.get('duploVinculo')?.value ?? null,
      diaPagamentoProLabore: this.form.get('diaPagamentoProLabore')?.value ?? null,
      dataHoraUsuario: new Date().toISOString()
    };

    this.isSaving = true;
    this.spinnerService.show('Salvando pró-labore...');
    this.sociosService.atualizarProLaboreSocio(request).subscribe({
      next: response => {
        this.isSaving = false;
        this.spinnerService.hide();
        this.toastService.setMensagemToast('Pró-labore atualizado com sucesso.', SeveridadeToast.Success);
        this.saved.emit(response);
      },
      error: error => {
        this.isSaving = false;
        this.spinnerService.hide();
        this.toastService.setMensagemToastErro(error, 'Não foi possível atualizar o pró-labore.');
      }
    });
  }

  fechar(): void {
    this.close.emit();
  }

  get competenciaPreview(): string {
    const competencia = this.form.get('competencia')?.value;
    if (!competencia) {
      return '';
    }

    return competencia.toLocaleDateString('pt-BR', {
      month: '2-digit',
      year: 'numeric'
    });
  }

  get isSaveDisabled(): boolean {
    if (this.isSaving || this.isLoading || !this.socio) {
      return true;
    }

    const vaiRetirarProLabore = this.form.get('vaiRetirarProLabore')?.value;

    if (vaiRetirarProLabore === null || vaiRetirarProLabore === undefined) {
      return true;
    }

    if (!vaiRetirarProLabore) {
      return false;
    }

    const competencia = this.form.get('competencia')?.value;
    if (!this.isValidDate(competencia)) {
      return true;
    }

    const proLaboreMensal = this.form.get('proLaboreMensal')?.value;
    const participacaoSocietaria = this.form.get('participacaoSocietaria')?.value;
    const duploVinculo = this.form.get('duploVinculo')?.value;
    const diaPagamentoProLabore = this.form.get('diaPagamentoProLabore')?.value;
    const inssSobreProLabore = this.form.get('inssSobreProLabore')?.value;
    const irrfSobreProLabore = this.form.get('irrfSobreProLabore')?.value;
    const valorLiquidoProLabore = this.form.get('valorLiquidoProLabore')?.value;

    return (
      !proLaboreMensal ||
      participacaoSocietaria === null ||
      participacaoSocietaria === undefined ||
      duploVinculo === null ||
      duploVinculo === undefined ||
      diaPagamentoProLabore === null ||
      diaPagamentoProLabore === undefined ||
      inssSobreProLabore === null ||
      inssSobreProLabore === undefined ||
      irrfSobreProLabore === null ||
      irrfSobreProLabore === undefined ||
      valorLiquidoProLabore === null ||
      valorLiquidoProLabore === undefined
    );
  }

  private carregarCompetenciaAtual(): void {
    const competenciaAtual = new Date(this.hoje.getFullYear(), this.hoje.getMonth(), 1);
    this.form.get('competencia')?.setValue(competenciaAtual);
    this.onCompetenciaChange(competenciaAtual);
  }

  private preencherFormulario(response: ProLaboreSocioEdicaoResponse): void {
    const competencia = new Date(response.competenciaAno, response.competenciaMes - 1, 1);
    const possuiDadosResposta =
      response.vaiRetirarProLabore ||
      (response.proLaboreMensal ?? 0) > 0 ||
      (response.inssSobreProLabore ?? 0) > 0 ||
      (response.irrfSobreProLabore ?? 0) > 0 ||
      (response.valorLiquidoProLabore ?? 0) > 0 ||
      (response.participacaoSocietaria !== null && response.participacaoSocietaria !== undefined) ||
      (response.duploVinculo !== null && response.duploVinculo !== undefined) ||
      (response.diaPagamentoProLabore !== null && response.diaPagamentoProLabore !== undefined);
    const possuiDadosSocioSelecionado =
      (this.socio?.vrSalFx ?? 0) > 0 ||
      (this.socio?.inssAproximado ?? 0) > 0 ||
      (this.socio?.baseInss ?? 0) > 0 ||
      (this.socio?.contribuicaoInss ?? 0) > 0 ||
      (this.socio?.participacaoSocietaria !== null && this.socio?.participacaoSocietaria !== undefined) ||
      (this.socio?.duploVinculo !== null && this.socio?.duploVinculo !== undefined) ||
      (this.socio?.diaPagamentoProLabore !== null && this.socio?.diaPagamentoProLabore !== undefined);
    const vaiRetirarProLabore = possuiDadosResposta || possuiDadosSocioSelecionado;

    this.form.patchValue({
      competencia,
      vaiRetirarProLabore,
      proLaboreMensal: response.proLaboreMensal ?? this.socio?.vrSalFx ?? null,
      inssSobreProLabore: response.inssSobreProLabore ?? this.socio?.inssAproximado ?? null,
      irrfSobreProLabore: response.irrfSobreProLabore ?? this.socio?.baseInss ?? null,
      valorLiquidoProLabore: response.valorLiquidoProLabore ?? this.socio?.contribuicaoInss ?? null,
      participacaoSocietaria: response.participacaoSocietaria ?? this.socio?.participacaoSocietaria ?? null,
      duploVinculo: response.duploVinculo ?? this.socio?.duploVinculo ?? null,
      diaPagamentoProLabore: response.diaPagamentoProLabore ?? this.socio?.diaPagamentoProLabore ?? null,
      dataInicioValidade: competencia
    });

    this.onVaiRetirarChange(vaiRetirarProLabore);
  }

  private isValidDate(value: Date | null | undefined): value is Date {
    return value instanceof Date && !Number.isNaN(value.getTime());
  }

  private sincronizarDataInicioValidadeComCompetencia(): void {
    const competencia = this.form.get('competencia')?.value;
    if (this.isValidDate(competencia)) {
      this.form.get('dataInicioValidade')?.setValue(competencia);
    }
  }

  private resetForm(): void {
    this.form.reset({
      competencia: null,
      vaiRetirarProLabore: false,
      proLaboreMensal: null,
      inssSobreProLabore: null,
      irrfSobreProLabore: null,
      valorLiquidoProLabore: null,
      participacaoSocietaria: null,
      duploVinculo: null,
      diaPagamentoProLabore: null,
      dataInicioValidade: null
    });
  }
}
